import { useState, useEffect } from 'react';
import { api } from '../api/client';

// SVG layout — portrait-friendly aspect ratio (~1.5:1)
const W = 480, H = 320;
const PAD = { l: 52, r: 16, t: 24, b: 40 };
const PW = W - PAD.l - PAD.r;
const PH = H - PAD.t - PAD.b;

const PROJECT_DAYS = 30;

function fmt(v) {
  return v.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

function fmtShort(v) {
  const abs = Math.abs(v);
  const prefix = v < 0 ? '-' : '';
  if (abs >= 1000) return `${prefix}£${(abs / 1000).toFixed(1)}k`;
  return `${prefix}£${abs.toFixed(0)}`;
}

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function linearSlope(values) {
  const n = values.length;
  if (n < 2) return values[0] || 0;
  const sumX = values.reduce((s, _, i) => s + i, 0);
  const sumY = values.reduce((s, v) => s + v, 0);
  const sumXY = values.reduce((s, v, i) => s + i * v, 0);
  const sumX2 = values.reduce((s, _, i) => s + i * i, 0);
  return (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
}

export default function NetReturnChart({ mode }) {
  const [overTime, setOverTime] = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tooltip, setTooltip]   = useState(null);
  const [lookback, setLookback] = useState(14);

  const isBatch = mode === 'current_batch';
  const qs = isBatch ? '?mode=current_batch' : '';

  useEffect(() => {
    Promise.all([
      api.get(`/admin/sales/over-time${qs}`),
      api.get(`/admin/sales/summary${qs}`),
    ]).then(([ot, s]) => {
      setOverTime(ot);
      setSummary(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [qs]);

  if (loading) {
    return (
      <div className="card">
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>Loading…</p>
      </div>
    );
  }

  if (!overTime.length) {
    return (
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>{isBatch ? 'Current Batch Projection' : 'Net Return Projection'}</h3>
        <div className="empty-state" style={{ padding: 40 }}>{isBatch ? 'No sales data for current batch' : 'No sales data yet'}</div>
      </div>
    );
  }

  // Build cumulative net income series
  let cumulative = 0;
  const actual = overTime.map(d => {
    cumulative += parseFloat(d.net_income);
    return {
      date: d.date,
      cum: cumulative,
      net: parseFloat(d.net_income),
      revenue: parseFloat(d.revenue),
      cost: parseFloat(d.cost),
    };
  });

  const lastDate     = actual[actual.length - 1].date;
  const cumulativeNow = actual[actual.length - 1].cum;

  // Slope from selected lookback window
  const lookbackSlice = overTime.slice(-lookback);
  const dailyNets     = lookbackSlice.map(d => parseFloat(d.net_income));
  const slope         = linearSlope(dailyNets);

  // Scenario slopes
  const bestSlope  = slope > 0 ? slope * 1.5 : slope * 0.5;
  const worstSlope = slope > 0 ? slope * 0.5 : slope * 1.5;

  // Confidence band (±1σ)
  const mean   = dailyNets.reduce((s, v) => s + v, 0) / dailyNets.length;
  const stdDev = Math.sqrt(dailyNets.reduce((s, v) => s + (v - mean) ** 2, 0) / dailyNets.length);

  // Projection points (centre, best, worst)
  const projected = [];
  for (let i = 1; i <= PROJECT_DAYS; i++) {
    projected.push({
      date: addDays(lastDate, i),
      cum:  cumulativeNow + slope * i,
      best: cumulativeNow + bestSlope * i,
      worst: cumulativeNow + worstSlope * i,
    });
  }

  const projEnd      = cumulativeNow + slope * PROJECT_DAYS;
  const projEndBest  = cumulativeNow + bestSlope * PROJECT_DAYS;
  const projEndWorst = cumulativeNow + worstSlope * PROJECT_DAYS;

  // Break-even projection (only if currently negative and trend is positive)
  let breakEvenDay = null;
  if (cumulativeNow < 0 && slope > 0) {
    const days = Math.ceil(-cumulativeNow / slope);
    if (days <= PROJECT_DAYS) breakEvenDay = days;
  }

  // Milestone markers — best and worst single days
  const bestDay  = actual.reduce((a, b) => b.net > a.net ? b : a, actual[0]);
  const worstDay = actual.reduce((a, b) => b.net < a.net ? b : a, actual[0]);
  const showWorstMarker = worstDay.net < 0 && worstDay !== bestDay;

  // Combine for scaling
  const allCums = [
    ...actual.map(p => p.cum),
    ...projected.map(p => p.cum),
    ...projected.map(p => p.best),
    ...projected.map(p => p.worst),
  ];
  const minY   = Math.min(0, ...allCums);
  const maxY   = Math.max(...allCums, 1);
  const rangeY = maxY - minY || 1;

  const totalPts = actual.length + projected.length;
  const toX = i => PAD.l + (i / (totalPts - 1)) * PW;
  const toY = v => PAD.t + PH - ((v - minY) / rangeY) * PH;

  // Y-axis grid
  const gridSteps = 4;
  const grids = Array.from({ length: gridSteps + 1 }, (_, i) => {
    const v = minY + (rangeY / gridSteps) * i;
    return { v, y: toY(v) };
  });

  // Polylines
  const projOffset = actual.length - 1;
  const actualPts  = actual.map((p, i) => `${toX(i).toFixed(1)},${toY(p.cum).toFixed(1)}`).join(' ');

  const projLine = (getCum) =>
    [actual[actual.length - 1], ...projected]
      .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY(getCum(p, i)).toFixed(1)}`).join(' ');

  const centreProj = projLine((p) => p.cum ?? cumulativeNow);
  const bestProj   = projLine((p, i) => i === 0 ? cumulativeNow : p.best);
  const worstProj  = projLine((p, i) => i === 0 ? cumulativeNow : p.worst);

  // Confidence band polygon
  const upperBand = [actual[actual.length - 1], ...projected]
    .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY((p.cum ?? cumulativeNow) + stdDev * Math.sqrt(i)).toFixed(1)}`).join(' ');
  const lowerBandRev = [actual[actual.length - 1], ...projected]
    .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY((p.cum ?? cumulativeNow) - stdDev * Math.sqrt(i)).toFixed(1)}`).reverse().join(' ');
  const bandPolygon = upperBand + ' ' + lowerBandRev;

  // X labels
  const dividerX  = toX(actual.length - 1);
  const xLabelStep = Math.ceil(totalPts / 6);

  // Break-even marker x
  const breakEvenX = breakEvenDay !== null ? toX(actual.length - 1 + breakEvenDay) : null;

  // Milestone index lookups
  const bestDayIdx  = actual.findIndex(p => p.date === bestDay.date);
  const worstDayIdx = showWorstMarker ? actual.findIndex(p => p.date === worstDay.date) : -1;

  // Deduction breakdown
  const rewardCost   = summary ? parseFloat(summary.reward_cost || 0) : 0;
  const adminCost    = summary ? parseFloat(summary.admin_cost || 0) : 0;
  const writeOff     = summary ? parseFloat(summary.write_off_cost || 0) : 0;
  const totalRevenue = summary ? parseFloat(summary.total_revenue || 0) : 0;
  const leftToRecoup = summary ? parseFloat(summary.left_to_recoup || 0) : 0;
  const totalStock   = summary ? parseFloat(summary.total_stock_cost || 0) : 0;
  const recoupPct    = totalStock > 0 ? Math.min(100, ((totalStock - leftToRecoup) / totalStock * 100)).toFixed(0) : 100;
  const loyaltyPct   = totalRevenue > 0 ? ((rewardCost / totalRevenue) * 100).toFixed(1) : '0';
  const adminPct     = totalRevenue > 0 ? ((adminCost / totalRevenue) * 100).toFixed(1) : '0';

  const trendArrow = slope > 0 ? '↑' : slope < 0 ? '↓' : '→';
  const trendColor = slope > 0 ? 'var(--success)' : slope < 0 ? 'var(--danger)' : 'var(--text-muted)';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>{isBatch ? 'Current Batch Projection' : 'Net Return Projection'}</h3>
        {/* Lookback selector */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setLookback(d)}
              style={{
                padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                border: lookback === d ? 'none' : '1px solid var(--border)',
                background: lookback === d ? 'var(--accent)' : 'var(--bg-secondary)',
                color: lookback === d ? '#fff' : 'var(--text-muted)',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        {isBatch ? 'Current batch only · ' : ''}Cumulative net return after costs, rewards, admin use &amp; write-offs &middot; dashed = {PROJECT_DAYS}-day projection
      </p>

      {/* Summary chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: cumulativeNow >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {fmt(cumulativeNow)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Net Return Now</div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: trendColor }}>
            {trendArrow} {fmtShort(Math.abs(slope))}/day
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Trend ({lookback}d avg)</div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: projEnd >= cumulativeNow ? '#63b3ed' : 'var(--danger)' }}>
            {fmt(projEnd)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            In {PROJECT_DAYS} Days
            <span style={{ marginLeft: 4, color: projEnd >= cumulativeNow ? 'var(--success)' : 'var(--danger)' }}>
              ({projEnd >= cumulativeNow ? '+' : ''}{fmt(projEnd - cumulativeNow)})
            </span>
          </div>
        </div>

        {breakEvenDay !== null && (
          <div style={{ background: 'rgba(99,179,237,0.1)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px', border: '1px solid rgba(99,179,237,0.3)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#63b3ed' }}>~{breakEvenDay}d</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Projected Break-even</div>
          </div>
        )}

        {totalStock > 0 && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: leftToRecoup === 0 ? 'var(--success)' : 'var(--warning)' }}>
              {recoupPct}%
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Stock Recouped
              {leftToRecoup > 0 && <span style={{ display: 'block', color: 'var(--text-muted)' }}>{fmt(leftToRecoup)} remaining</span>}
            </div>
          </div>
        )}

        {rewardCost > 0 && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fbd38d' }}>
              {fmt(rewardCost)} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>({loyaltyPct}%)</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Loyalty Rewards</div>
          </div>
        )}

        {adminCost > 0 && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#d6bcfa' }}>
              {fmt(adminCost)} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>({adminPct}%)</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Admin Use</div>
          </div>
        )}

        {writeOff > 0 && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 120px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)' }}>{fmt(writeOff)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Write-offs</div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', minWidth: 320, height: 'auto' }}>
          {/* Y grid */}
          {grids.map(({ v, y }) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y}
                stroke="rgba(255,255,255,0.12)" strokeWidth={1} />
              <text x={PAD.l - 5} y={y + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">
                {fmtShort(v)}
              </text>
            </g>
          ))}

          {/* Zero line */}
          {minY < 0 && (
            <line x1={PAD.l} x2={W - PAD.r} y1={toY(0)} y2={toY(0)}
              stroke="var(--text-muted)" strokeWidth={1} strokeDasharray="3 2" opacity={0.5} />
          )}

          {/* Break-even vertical marker */}
          {breakEvenX !== null && (
            <g>
              <line x1={breakEvenX} x2={breakEvenX} y1={PAD.t} y2={PAD.t + PH}
                stroke="#63b3ed" strokeWidth={1} strokeDasharray="3 3" opacity={0.6} />
              <text x={breakEvenX + 4} y={PAD.t + PH - 6} fontSize={10} fill="#63b3ed" opacity={0.9}>
                b/e ~{breakEvenDay}d
              </text>
            </g>
          )}

          {/* Projected confidence band */}
          <polygon points={bandPolygon} fill="rgba(99,179,237,0.08)" />

          {/* Worst / best scenario lines */}
          <polyline points={worstProj} fill="none"
            stroke="var(--danger)" strokeWidth={1} strokeDasharray="3 4" opacity={0.35}
            strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={bestProj} fill="none"
            stroke="var(--success)" strokeWidth={1} strokeDasharray="3 4" opacity={0.35}
            strokeLinejoin="round" strokeLinecap="round" />

          {/* Today divider */}
          <line x1={dividerX} x2={dividerX} y1={PAD.t} y2={PAD.t + PH}
            stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.6} />
          <text x={dividerX + 5} y={PAD.t + 13} fontSize={11} fill="var(--text-muted)">Today</text>

          {/* Projected line (dashed blue) */}
          <polyline points={centreProj} fill="none"
            stroke="#63b3ed" strokeWidth={2.5} strokeDasharray="7 4"
            strokeLinejoin="round" strokeLinecap="round" />

          {/* Actual line */}
          <polyline points={actualPts} fill="none"
            stroke={cumulativeNow >= 0 ? '#48bb78' : '#fc8181'} strokeWidth={3}
            strokeLinejoin="round" strokeLinecap="round" />

          {/* Best day marker */}
          {bestDayIdx >= 0 && (
            <g>
              <circle cx={toX(bestDayIdx)} cy={toY(bestDay.cum)} r={5}
                fill="#48bb78" stroke="var(--bg-card)" strokeWidth={2} />
              <text x={toX(bestDayIdx)} y={toY(bestDay.cum) - 9} textAnchor="middle"
                fontSize={10} fill="#48bb78">
                best day
              </text>
            </g>
          )}

          {/* Worst day marker */}
          {showWorstMarker && worstDayIdx >= 0 && (
            <g>
              <circle cx={toX(worstDayIdx)} cy={toY(worstDay.cum)} r={5}
                fill="#fc8181" stroke="var(--bg-card)" strokeWidth={2} />
              <text x={toX(worstDayIdx)} y={toY(worstDay.cum) + 17} textAnchor="middle"
                fontSize={10} fill="#fc8181">
                worst day
              </text>
            </g>
          )}

          {/* X axis */}
          <line x1={PAD.l} x2={W - PAD.r} y1={PAD.t + PH} y2={PAD.t + PH}
            stroke="var(--border)" strokeWidth={1} />

          {/* X labels */}
          {[...actual, ...projected].map((p, i) => {
            if (i !== 0 && i !== actual.length - 1 && i !== totalPts - 1 && i % xLabelStep !== 0) return null;
            return (
              <text key={i} x={toX(i)} y={PAD.t + PH + 16} textAnchor="middle"
                fontSize={11} fill={i >= actual.length ? '#63b3ed' : 'var(--text-muted)'}>
                {p.date.slice(5)}
              </text>
            );
          })}

          {/* Hover areas */}
          {actual.map((p, i) => {
            const slotW = Math.max(PW / totalPts, 6);
            return (
              <g key={i}>
                <rect
                  x={toX(i) - slotW / 2} y={PAD.t}
                  width={slotW} height={PH}
                  fill="transparent"
                  style={{ cursor: 'crosshair' }}
                  onMouseEnter={() => setTooltip({ i, p })}
                  onMouseLeave={() => setTooltip(null)}
                />
                {tooltip?.i === i && (
                  <circle cx={toX(i)} cy={toY(p.cum)} r={5}
                    fill={cumulativeNow >= 0 ? '#48bb78' : '#fc8181'} stroke="var(--bg-card)" strokeWidth={2} />
                )}
              </g>
            );
          })}

          {/* Tooltip */}
          {tooltip && (() => {
            const { i, p } = tooltip;
            const tipW = 180, tipH = 72;
            const tipX = Math.min(Math.max(toX(i) - tipW / 2, PAD.l), W - PAD.r - tipW);
            const tipY = PAD.t + 4;
            return (
              <g style={{ pointerEvents: 'none' }}>
                <rect x={tipX} y={tipY} width={tipW} height={tipH}
                  rx={5} fill="var(--bg-secondary)" stroke="var(--border)" strokeWidth={1.5} />
                <text x={tipX + 10} y={tipY + 16} fontSize={11} fontWeight={700} fill="var(--text-secondary)">
                  {p.date}
                </text>
                <text x={tipX + 10} y={tipY + 31} fontSize={11} fill="#48bb78">
                  Cumulative: {fmt(p.cum)}
                </text>
                <text x={tipX + 10} y={tipY + 46} fontSize={11} fill="var(--text-muted)">
                  Day net: {p.net >= 0 ? '+' : ''}{fmt(p.net)}
                </text>
                <text x={tipX + 10} y={tipY + 61} fontSize={10} fill="var(--text-muted)">
                  Rev: {fmt(p.revenue)}  ·  Cost: {fmt(p.cost)}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 20, height: 3, background: '#48bb78', borderRadius: 2 }} />
          <span style={{ color: 'var(--text-secondary)' }}>Actual</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#63b3ed" strokeDasharray="6,4" strokeWidth={2} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Projection (±1σ band)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#48bb78" strokeDasharray="3,4" strokeWidth={1} opacity={0.5} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Best case</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#fc8181" strokeDasharray="3,4" strokeWidth={1} opacity={0.5} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Worst case</span>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
        Projection uses a linear trend fitted to the last {lookback} days. Best/worst case = ±50% slope.
        Net return = sales profit minus reward redemptions, admin use &amp; write-offs.
        Confidence band widens over time (square-root scaling).
      </p>
    </div>
  );
}
